<template>
  <v-app>
    <!-- ヘッダー -->
    <main-header
      title="余韻の米蔵"
      sub-title=" - 八ヶ岳の余韻オンラインストア"
      @nav-click="openNavDrawer"
      @title-click="gotoTop"
    >
      <!-- ユーザーメニューボタン -->
      <user-menu-btn
        v-if="authUser"
        :menus="authUserMenus"
        :auth-user-name="authUser.name"
      />
      <!-- ログインボタン -->
      <icon-btn
        v-else
        icon="mdi-account"
        text="ログイン"
        :large="true"
        @btn-click="openLogin"
      ></icon-btn>

      <!-- カートボタン -->
      <icon-btn-with-badge
        class="cart-button"
        icon="mdi-cart"
        text="カート"
        :large="true"
        :counter="totalQty"
        @btn-click="gotoOrder"
      ></icon-btn-with-badge>
    </main-header>

    <!-- ナビゲーションドロワー -->
    <user-nav-drawer
      ref="userNavDrawer"
      :auth-user-name="authUser ? authUser.name : null"
      :menus="navMenus"
      @login-btn-click="openLogin"
    ></user-nav-drawer>

    <!-- ログインダイアログ -->
    <login-dialog
      ref="loginDialog"
      :login-action="login"
      :register-user-action="signup"
      @forget-password-click="openPasswordReset"
    />

    <!-- パスワードリセットダイアログ -->
    <password-reset-dialog
      ref="passwordResetDialog"
      :execute-action="resetPassword"
    />

    <!-- メインコンテンツ -->
    <v-main class="grey lighten-4 main-content">
      <!-- エラー時表示 -->
      <v-container v-if="errorCode" class="pa-5">
        <system-error
          :error-code="errorCode"
          :request-id="requestId"
          :error-message="errorMessage"
          @goto-top-click="refresh"
        ></system-error>
      </v-container>
      <!-- 正常時表示 -->
      <router-view v-else></router-view>
    </v-main>

    <!-- フッター -->
    <main-footer
      :copyright-text="`${new Date().getFullYear()} 株式会社 今井農園`"
    ></main-footer>

    <!-- アカウント登録時、パスワードリセット依頼時メッセージ -->
    <v-snackbar v-model="snackbar" :timeout="30000" bottom color="info" dark>
      入力したメールアドレス宛にメールを送信しました。<br />
      メールに記載されたリンクから処理を続行してください。
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          閉じる
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { mapGetters } from 'vuex';
import * as authApi from './api/modules/auth';
import { HttpStatuses } from './constants/komegura-config';
import { isSuccessStatus } from './utils/http';
import { buildAuthUserMenus, buildNavMenus } from './utils/menuFactory';
import LoginDialog from './components/feature/LoginDialog.vue';
import PasswordResetDialog from './components/feature/PasswordResetDialog.vue';
import MainHeader from './components/common/MainHeader.vue';
import MainFooter from './components/common/MainFooter.vue';
import SystemError from './components/common/SystemError.vue';
import UserMenuBtn from './components/common/UserMenuBtn.vue';
import UserNavDrawer from './components/common/UserNavDrawer.vue';
import IconBtn from './components/common/IconBtn.vue';
import IconBtnWithBadge from './components/common/IconBtnWithBadge.vue';

export default {
  name: 'App',
  components: {
    LoginDialog,
    PasswordResetDialog,
    MainHeader,
    MainFooter,
    SystemError,
    UserMenuBtn,
    UserNavDrawer,
    IconBtn,
    IconBtnWithBadge,
  },
  data: () => ({
    snackbar: false,
  }),
  computed: {
    ...mapGetters({
      authUser: 'auth/authUser',
      totalQty: 'cart/totalQty',
      errorCode: 'error/code',
      requestId: 'error/id',
      errorMessage: 'error/message',
    }),
    // ユーザーメニューボタン用
    authUserMenus() {
      return buildAuthUserMenus({
        authUser: this.authUser,
        gotoMyAccount: this.gotoMyAccount,
        groupMainte: this.groupMainte,
        logout: this.logout,
      });
    },
    // ナビゲーションドロワー用
    navMenus() {
      return buildNavMenus({
        authUser: this.authUser,
        totalQty: this.totalQty,
        gotoTop: this.gotoTop,
        gotoOrder: this.gotoOrder,
        groupMainte: this.groupMainte,
        gotoAbout: this.gotoAbout,
        gotoTerms: this.gotoTerms,
        gotoPolicy: this.gotoPolicy,
        gotoLaw: this.gotoLaw,
        gotoMyAccount: this.gotoMyAccount,
        logout: this.logout,
        masterMainte: this.masterMainte,
        noticeMainte: this.noticeMainte,
      });
    },
  },
  methods: {
    // トップ画面に遷移
    gotoTop() {
      this.$router.push('/').catch(() => {});
    },
    // ナビゲーションメニュー押下
    openNavDrawer() {
      this.$refs.userNavDrawer.open();
    },
    // ログインボタン押下
    openLogin() {
      this.$refs.loginDialog.open();
    },
    // パスワードを忘れた
    openPasswordReset() {
      this.$refs.passwordResetDialog.open();
    },
    // ログイン
    async login(authData) {
      // 商品情報を一旦クリア
      this.$store.dispatch('merchandise/resetItemDetail');

      await this.$store.dispatch('auth/login', authData);

      this.$refs.userNavDrawer.close();
    },
    // アカウント作成してログイン
    async signup(authData) {
      // 商品情報を一旦クリア
      this.$store.dispatch('merchandise/resetItemDetail');

      if (await this.$store.dispatch('auth/signup', authData)) {
        this.snackbar = true;
      }

      this.$refs.userNavDrawer.close();
    },
    // グループ管理画面に遷移
    groupMainte() {
      this.$router.push('/groups').catch(() => {});
    },
    // マスタ管理画面に遷移
    masterMainte() {
      this.$router.push('/maintenance').catch(() => {});
    },
    // マスタ管理画面に遷移
    noticeMainte() {
      this.$router.push('/notice').catch(() => {});
    },
    // ログアウト
    async logout() {
      this.$refs.userNavDrawer.close();
      this.$store.dispatch('auth/clearOrderHistories');
      this.$store.dispatch('auth/clearAddressLists');
      this.$store.dispatch('auth/clearGroupMembers');
      this.$store.dispatch('auth/clearGroup');
      this.$store.dispatch('cart/clearCart');
      this.$store.dispatch('merchandise/resetItemDetail');
      await this.$store.dispatch('auth/logout');
      this.gotoTop();
    },
    // パスワードリセット依頼
    async resetPassword(email) {
      const result = await authApi.requestPasswordReset(email);
      if (isSuccessStatus(result.statusCode)) {
        this.snackbar = true;
      } else if (result.statusCode === HttpStatuses.NOT_FOUND) {
        throw new Error('指定されたメールアドレスは存在しません。');
      } else {
        this.$store.dispatch('error/setError', {
          code: result.statusCode,
          id: result.requestId,
          message: result.response,
        });
      }
    },
    // 注文画面に遷移
    gotoOrder() {
      this.$router.push('/order').catch(() => {});
    },
    // 今井農園についてページに遷移
    gotoAbout() {
      this.$router.push('/about').catch(() => {});
    },
    // 利用規約画面に遷移
    gotoTerms() {
      this.$router.push('/terms').catch(() => {});
    },
    // プライバシーポリシー画面に遷移
    gotoPolicy() {
      this.$router.push('/policy').catch(() => {});
    },
    // 特商法に基づく表記画面に遷移
    gotoLaw() {
      this.$router.push('/law').catch(() => {});
    },
    // マイアカウントに遷移
    gotoMyAccount() {
      this.$router.push('/auth').catch(() => {});
    },
    async refresh() {
      await this.$store.dispatch('auth/clearAuth');
      this.$store.dispatch('error/clearError');
      window.location = '/';
    },
  },
};
</script>

<style>
.main-content {
  background: url(../public/tree.png) repeat;
}
@media screen and (max-width: 768px) {
  /* ユーザーメニューの非表示 */
  .v-app-bar .v-btn:not(.v-app-bar__nav-icon):not(.cart-button) {
    display: none;
  }
}
@media screen and (max-width: 510px) {
  /* サブタイトルの非表示 */
  .v-toolbar__title span {
    display: none;
  }
}
</style>
